import { getSessionStorage } from "./storage";

interface ApiConfig {
  method: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  body?: any;
}

interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "https://api2.dev.jpki-dsec.jp";
const API_KEY = process.env.NEXT_PUBLIC_API_KEY;

if (!API_KEY) {
  throw new Error("API_KEY is not defined in environment variables");
}

export const apiClient = async <T>(
  endpoint: string,
  config: ApiConfig
): Promise<ApiResponse<T>> => {
  try {
    // セッションストレージから認証情報を取得
    const authToken = getSessionStorage("authToken");

    // リクエストヘッダーの設定
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
      "X-API-Key": API_KEY,
      ...config.headers,
    };

    // リクエストの設定
    const requestConfig: RequestInit = {
      method: config.method,
      headers,
      credentials: "include", // Cookieを送信するために必要
      ...(config.body && { body: JSON.stringify(config.body) }),
    };

    // APIリクエストの実行
    const response = await fetch(`${API_BASE_URL}${endpoint}`, requestConfig);

    // レスポンスの処理
    if (!response.ok) {
      // エラー応答の本文(error_info)を呼び出し元で判定できるように保持する。
      // JSON以外や空の本文が返る場合もあるため、取得できなければ undefined のままとする。
      let errorData: unknown = undefined;
      try {
        errorData = await response.json();
      } catch {
        errorData = undefined;
      }

      const error = new Error(
        `API Error: ${response.status} ${response.statusText}`
      ) as Error & {
        status?: number;
        data?: unknown;
        response?: {
          status?: number;
          statusText?: string;
          data?: unknown;
        };
      };

      error.status = response.status;
      error.data = errorData;
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
      };

      throw error;
    }

    const data = await response.json();

    return {
      data,
      status: response.status,
      statusText: response.statusText,
    };
  } catch (error) {
    console.error("API Request Error:", error);
    throw error;
  }
};

// 使用例
export const getData = async <T>(endpoint: string) => {
  return apiClient<T>(endpoint, { method: "GET" });
};

export const postData = async <T>(endpoint: string, data: any) => {
  return apiClient<T>(endpoint, { method: "POST", body: data });
};

export const putData = async <T>(endpoint: string, data: any) => {
  return apiClient<T>(endpoint, { method: "PUT", body: data });
};

export const deleteData = async <T>(endpoint: string) => {
  return apiClient<T>(endpoint, { method: "DELETE" });
};
